import { GraphDto } from "@custom-types/graphs"
import {
  getDefaultValues,
  getEligibleWhatIfContractOptions,
} from "@features/run/RunGroupDialog/helpers"
import { LinkedGraphMode } from "@features/run/RunGroupDialog/RunGroupForm/LinkedGraphMode"
import { RunGroupFieldContractKey } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldContractKey"
import { RunGroupFieldDescription } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldDescription"
import { RunGroupFieldEndDate } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldEndDate"
import { RunGroupFieldName } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldName"
import { RunGroupFieldNumberOfSamplings } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldNumberOfSamplings"
import { RunGroupFieldScenarios } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldScenarios"
import { RunGroupFieldSeed } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldSeed"
import { RunGroupFieldStartDate } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldStartDate"
import { RunGroupFieldValidate } from "@features/run/RunGroupDialog/RunGroupForm/RunGroupFieldValidate"
import { getValidationSchema } from "@features/run/RunGroupDialog/schema"
import { createRunGroupResolver } from "@features/run/RunGroupDialog/createRunGroupResolver"
import { useRunGroupFormSync } from "@features/run/RunGroupDialog/useRunGroupFormSync"
import { Stack } from "@mui/material"
import { useAtomValue } from "jotai"
import { permissionsAtom } from "@store/authAtoms"
import { HTMLAttributes, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { InferType } from "yup"

export type RunGroupFormValues = InferType<ReturnType<typeof getValidationSchema>>

export type RunGroupFormProps = {
  graph: GraphDto
  onSubmit: (values: RunGroupFormValues) => void
  id: string
  children: React.ReactNode
  formProps?: HTMLAttributes<HTMLFormElement>
}

const RunGroupForm = ({ onSubmit, graph, id, children, formProps }: RunGroupFormProps) => {
  const permissions = useAtomValue(permissionsAtom)

  // only whatif permissions allow a user to run sampling, even if they can view this item
  const contractOptions = useMemo(
    () => getEligibleWhatIfContractOptions(graph.base_graph_family_key, permissions),
    [graph.base_graph_family_key, permissions],
  )
  const defaultValues = useMemo(
    () => getDefaultValues(graph, contractOptions),
    [graph, contractOptions],
  )
  const resolver = useMemo(() => createRunGroupResolver(graph, permissions), [graph, permissions])

  const formMethods = useForm<RunGroupFormValues>({
    defaultValues,
    resolver,
    mode: "onChange",
    reValidateMode: "onChange",
  })

  useRunGroupFormSync({
    graph,
    reset: formMethods.reset,
    defaultValues,
  })

  const { handleSubmit } = formMethods

  return (
    <FormProvider {...formMethods}>
      <form autoComplete="off" noValidate id={id} onSubmit={handleSubmit(onSubmit)} {...formProps}>
        <Stack direction="column" spacing={2}>
          {children}
        </Stack>
      </form>
    </FormProvider>
  )
}

RunGroupForm.Name = RunGroupFieldName
RunGroupForm.NumberOfSamplings = RunGroupFieldNumberOfSamplings
RunGroupForm.Seed = RunGroupFieldSeed
RunGroupForm.ContractKey = RunGroupFieldContractKey
RunGroupForm.StartDate = RunGroupFieldStartDate
RunGroupForm.EndDate = RunGroupFieldEndDate
RunGroupForm.Scenario = RunGroupFieldScenarios
RunGroupForm.Description = RunGroupFieldDescription
RunGroupForm.LinkedGraphMode = LinkedGraphMode
RunGroupForm.Validate = RunGroupFieldValidate

export default RunGroupForm
